/**
 * Simplified initialization command
 * Replaces the complex multi-step init with a streamlined approach
 */

import { Command } from 'commander';
import pc from 'picocolors';
import inquirer from 'inquirer';
import { existsSync, writeFileSync, mkdirSync } from 'fs';
import { join, dirname } from 'path';
import { detectFramework } from '../utils/framework-detector';
import { getErrorMessage } from '../utils/error-type-guards';
import { getQuickStartTemplate } from '../templates/quick-start-template';
import { quickTasksRouteTemplate } from '../templates/quick-tasks-route';
import { quickStreamRouteTemplate } from '../templates/quick-stream-route';
import { quickFilesRouteTemplate } from '../templates/quick-files-route';
import { quickFilesContentRouteTemplate } from '../templates/quick-files-content-route';
import { getConfigTemplate } from '../templates/config-template';
import { getProviderTemplate } from '../templates/provider-template';
import { getEnvTemplate } from '../templates/env-template';
import { 
  getTasksRouteTemplate, 
  getFilesRouteTemplate, 
  getAIChatRouteTemplate, 
  getSSEStreamRouteTemplate,
  getPagesTasksTemplate,
  getPagesFilesTemplate 
} from '../templates/api-routes-templates';
import { readFileSync } from 'fs';
import { TailwindConfigUpdater } from '../utils/tailwind-config-updater';

export interface SimpleInitOptions {
  level?: 'quick' | 'standard' | 'advanced';
  yes?: boolean;
  force?: boolean;
  verbose?: boolean;
}

export const simpleInitCommand = new Command('init')
  .description('Initialize Vibeship DevTools (simplified)')
  .option('-l, --level <level>', 'setup level: quick, standard, or advanced', 'quick')
  .option('-y, --yes', 'skip confirmation prompts')
  .option('-f, --force', 'overwrite existing files')
  .action(async (options: SimpleInitOptions) => {
    console.log(pc.cyan('\n🚀 Vibeship DevTools - Simplified Setup\n'));

    try {
      // Detect framework
      const framework = await detectFramework();
      
      if (!framework.isNextJs) {
        console.log(pc.red('❌ This command requires a Next.js project'));
        console.log(pc.dim('   Please run this in a Next.js project directory\n'));
        process.exit(1);
      }

      console.log(pc.green(`✓ Detected Next.js ${framework.version} (${framework.isAppRouter ? 'App' : 'Pages'} Router)`));

      // Ask for setup level if not provided
      let { level } = options;
      if (!level && !options.yes) {
        const { selectedLevel } = await inquirer.prompt([{
          type: 'list',
          name: 'selectedLevel',
          message: 'Choose your setup level:',
          choices: [
            {
              name: '🚀 Quick Start (1 file, 10 seconds)',
              value: 'quick',
              short: 'Quick'
            },
            {
              name: '⚙️  Standard (3 files, full features)',
              value: 'standard',
              short: 'Standard'
            },
            {
              name: '🔧 Advanced (full control, all options)',
              value: 'advanced',
              short: 'Advanced'
            }
          ],
          default: 'quick'
        }]);
        level = selectedLevel;
      }

      // Perform setup based on level
      const startTime = Date.now();
      
      switch (level) {
        case 'quick':
          await performQuickSetup(framework, options);
          break;
        case 'standard':
          await performStandardSetup(framework, options);
          break;
        case 'advanced':
          await performAdvancedSetup(framework, options);
          break;
        default:
          throw new Error(`Unknown setup level: ${level}`);
      }

      // Update Tailwind config
      console.log(pc.blue('\n🎨 Updating Tailwind configuration...'));
      const tailwindUpdated = await TailwindConfigUpdater.updateTailwindConfig({
        projectRoot: process.cwd(),
        force: options.force
      });
      
      const duration = Date.now() - startTime;
      
      console.log(pc.green(`\n✅ Setup completed in ${duration}ms!`));
      showNextSteps(level, framework, tailwindUpdated);

    } catch (error) {
      console.log(pc.red('\n❌ Setup failed:'));
      console.log(pc.red(`   ${getErrorMessage(error)}\n`));
      
      showManualInstructions();
      process.exit(1);
    }
  });

export async function performQuickSetup(framework: any, options: SimpleInitOptions) {
  console.log(pc.blue('\n📝 Creating API routes...'));
  
  if (framework.isAppRouter) {
    // For App Router, create the essential route files
    const apiBase = framework.hasSrcDir ? 'src/app/api/vibeship' : 'app/api/vibeship';
    
    // Tasks route
    await createFile(
      `${apiBase}/tasks/route.ts`,
      quickTasksRouteTemplate,
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/tasks/route.ts`));
    
    // SSE stream route
    await createFile(
      `${apiBase}/tasks/stream/route.ts`,
      quickStreamRouteTemplate,
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/tasks/stream/route.ts`));
    
    // Files route
    await createFile(
      `${apiBase}/files/route.ts`,
      quickFilesRouteTemplate,
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/files/route.ts`));
    
    // File content route
    await createFile(
      `${apiBase}/files/content/route.ts`,
      quickFilesContentRouteTemplate,
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/files/content/route.ts`));
  } else {
    // For Pages Router, create a single API file
    const apiPath = framework.hasSrcDir ? 'src/pages/api/vibeship.ts' : 'pages/api/vibeship.ts';
    await createFile(
      apiPath,
      getQuickStartTemplate(framework),
      options.force
    );
    console.log(pc.green(`✓ Created ${apiPath}`));
  }
}

export async function performStandardSetup(framework: any, options: SimpleInitOptions) {
  console.log(pc.blue('\n📝 Creating files...'));
  
  // 1. Create separate API routes for different endpoints
  const apiBase = framework.isAppRouter 
    ? (framework.hasSrcDir ? 'src/app/api/vibeship' : 'app/api/vibeship')
    : (framework.hasSrcDir ? 'src/pages/api/vibeship' : 'pages/api/vibeship');

  // For App Router, create route handlers
  if (framework.isAppRouter) {
    // Tasks route
    await createFile(
      `${apiBase}/tasks/route.ts`,
      getTasksRouteTemplate(),
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/tasks/route.ts`));

    // Files route
    await createFile(
      `${apiBase}/files/route.ts`,
      getFilesRouteTemplate(),
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/files/route.ts`));

    // AI chat route
    await createFile(
      `${apiBase}/ai/chat/route.ts`,
      getAIChatRouteTemplate(),
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/ai/chat/route.ts`));

    // SSE stream route
    await createFile(
      `${apiBase}/tasks/stream/route.ts`,
      getSSEStreamRouteTemplate(),
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/tasks/stream/route.ts`));
  } else {
    // For Pages Router, create individual API files
    await createFile(
      `${apiBase}/tasks.ts`,
      getPagesTasksTemplate(),
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/tasks.ts`));

    await createFile(
      `${apiBase}/files.ts`,
      getPagesFilesTemplate(),
      options.force
    );
    console.log(pc.green(`✓ Created ${apiBase}/files.ts`));
  }

  // 2. Configuration file
  await createFile(
    'vibeship.config.ts',
    getConfigTemplate(),
    options.force
  );
  console.log(pc.green('✓ Created vibeship.config.ts'));

  // 3. Provider setup example
  await createFile(
    'components/VibeshipSetup.tsx',
    getProviderTemplate(),
    options.force
  );
  console.log(pc.green('✓ Created components/VibeshipSetup.tsx'));

  // 4. Environment variables template
  await createFile(
    '.env.local.example',
    getEnvTemplate(),
    options.force
  );
  console.log(pc.green('✓ Created .env.local.example'));

  // 5. Update layout file to include provider
  await updateLayoutFile(framework, options);
}

async function performAdvancedSetup(framework: any, options: SimpleInitOptions) {
  console.log(pc.yellow('\n🔧 Advanced setup mode...'));
  console.log(pc.dim('   This will use the full traditional setup process\n'));
  
  // Import and run the original init command
  const { initCommand } = await import('./init');
  return initCommand.parseAsync(['init', '--force'], { from: 'user' });
}

export async function createFile(filePath: string, content: string, force: boolean = false) {
  if (existsSync(filePath) && !force) {
    throw new Error(`File ${filePath} already exists. Use --force to overwrite.`);
  }

  // Ensure directory exists
  const dir = dirname(filePath);
  if (!existsSync(dir)) {
    mkdirSync(dir, { recursive: true });
  }

  writeFileSync(filePath, content, 'utf8');
}




function showNextSteps(level: string, framework: any, tailwindUpdated: boolean) {
  console.log(pc.cyan('\n📋 Next Steps:\n'));

  if (level === 'quick') {
    console.log(pc.white('1. Install the Vibeship DevTools package:'));
    console.log(pc.dim('   npm install @vibeship/devtools\n'));
    
    console.log(pc.white('2. Add DevTools to your layout:'));
    console.log(pc.dim('   import { VibeshipProviderWithDeps } from "@vibeship/devtools";'));
    console.log(pc.dim('   // Wrap your app with <VibeshipProviderWithDeps>\n'));
  } else if (level === 'standard') {
    console.log(pc.white('1. Install the Vibeship DevTools package:'));
    console.log(pc.dim('   npm install @vibeship/devtools\n'));
    
    console.log(pc.white('2. Your layout has been automatically updated!'));
    console.log(pc.dim('   Check your layout file to see the provider integration\n'));
  }

  console.log(pc.white('3. Start your development server:'));
  console.log(pc.dim('   npm run dev\n'));

  console.log(pc.white('4. Test the API:'));
  console.log(pc.dim('   curl http://localhost:3000/api/vibeship/health\n'));
  
  if (!tailwindUpdated) {
    console.log(pc.yellow('\n⚠️  Note: Could not update Tailwind config automatically.'));
    console.log(pc.white('   Please add these paths to your tailwind.config.ts content array:'));
    console.log(pc.dim('   "./node_modules/@vibeship/ui/dist/**/*.{js,mjs}",'));
    console.log(pc.dim('   "./node_modules/@vibeship/devtools/dist/**/*.{js,mjs}"\n'));
  }

  console.log(pc.cyan('🎉 You\'re all set!'));
  console.log(pc.dim('   Documentation: https://vibeship.dev/docs\n'));
}

function showManualInstructions() {
  console.log(pc.yellow('\n🔧 Manual Setup Instructions:\n'));
  
  console.log(pc.white('1. Create the API route file:'));
  console.log(pc.dim('   app/api/vibeship/route.ts (App Router)'));
  console.log(pc.dim('   pages/api/vibeship.ts (Pages Router)\n'));
  
  console.log(pc.white('2. Add this content:'));
  console.log(pc.dim('   export * from "@vibeship/devtools/api";\n'));
  
  console.log(pc.white('3. Install dependencies:'));
  console.log(pc.dim('   npm install @vibeship/ui\n'));
  
  console.log(pc.cyan('Need help? Visit: https://vibeship.dev/docs/troubleshooting\n'));
}

async function updateLayoutFile(framework: any, options: SimpleInitOptions) {
  try {
    let layoutPath: string;
    let layoutContent: string;
    
    if (framework.isAppRouter) {
      // App Router layout
      layoutPath = framework.hasSrcDir ? 'src/app/layout.tsx' : 'app/layout.tsx';
    } else {
      // Pages Router _app
      layoutPath = framework.hasSrcDir ? 'src/pages/_app.tsx' : 'pages/_app.tsx';
    }
    
    if (!existsSync(layoutPath)) {
      console.log(pc.yellow(`⚠️  Could not find ${layoutPath} to update automatically`));
      console.log(pc.dim('   Please manually add VibeshipProviderWithDeps to your layout\n'));
      return;
    }
    
    // Read existing layout
    layoutContent = readFileSync(layoutPath, 'utf8');
    
    // Check if already has Vibeship provider
    if (layoutContent.includes('VibeshipProviderWithDeps') || layoutContent.includes('VibeshipProvider')) {
      console.log(pc.dim('✓ Layout already includes Vibeship provider'));
      return;
    }
    
    // Add import statement
    const importStatement = `import { VibeshipProviderWithDeps } from '@vibeship/devtools';\n`;
    
    if (framework.isAppRouter) {
      // App Router: Add after imports
      const importMatch = layoutContent.match(/(import[^;]+;\n)+/);
      if (importMatch) {
        layoutContent = layoutContent.replace(
          importMatch[0],
          importMatch[0] + importStatement
        );
      } else {
        layoutContent = importStatement + layoutContent;
      }
      
      // Wrap children with provider
      layoutContent = layoutContent.replace(
        /({children})/g,
        `<VibeshipProviderWithDeps>\n          $1\n        </VibeshipProviderWithDeps>`
      );
    } else {
      // Pages Router: Add after imports
      const importMatch = layoutContent.match(/(import[^;]+;\n)+/);
      if (importMatch) {
        layoutContent = layoutContent.replace(
          importMatch[0],
          importMatch[0] + importStatement
        );
      } else {
        layoutContent = importStatement + layoutContent;
      }
      
      // Wrap Component with provider
      layoutContent = layoutContent.replace(
        /(<Component[^>]*\/?>)/g,
        `<VibeshipProviderWithDeps>\n        $1\n      </VibeshipProviderWithDeps>`
      );
    }
    
    // Write updated layout
    writeFileSync(layoutPath, layoutContent, 'utf8');
    console.log(pc.green(`✓ Updated ${layoutPath} with Vibeship provider`));
    
  } catch (error) {
    console.log(pc.yellow('⚠️  Could not automatically update layout file'));
    console.log(pc.dim(`   Error: ${getErrorMessage(error)}\n`));
    console.log(pc.dim('   Please manually add VibeshipProviderWithDeps to your layout\n'));
  }
}